import { Item, StockTransactionCreate, InternalReceiptCreate, InventoryPrecheckRequest, InventoryPrecheckResponse, Account, AccountLedgerResponse, AccountRemapPreview, AccountRemapResult, AccountUpsertPayload, JournalEntryCreate, FiscalPeriod, FiscalPeriodCreate, FiscalPeriodStatus, BOM, BOMCreate, WorkOrder, WorkOrderCreate, WorkOrderRequirementsResponse, WorkOrderProductionEntryRequest, WorkOrderProductionEntryResponse, WorkOrderMaterialsResponse, WorkOrderUnplannedMaterialCreate, WorkOrderDailyEntryRequest, WorkOrderDailyEntryResponse, WorkOrderDailyEntriesListResponse, WorkOrderSubPartCreate, WorkOrderSubPartUpdate, WorkOrderItem, WorkOrderItemMetadataUpdate, WorkOrderShiftEntryRequest, WorkOrderShiftEntryResponse, WorkOrderMaterialMovementRequest, WorkOrderMaterialMovementResponse, WorkOrderMaterialIssueBoardResponse, Quote, StockValuationRecord, JobProfitabilityRecord, StockMove, StockAdjustmentCreate, VATSummary, DashboardSummary, ExecutiveDashboardResponse, ExecutiveModuleDashboardResponse, SystemSetting, PrintSignatureStampMetadata, DatabaseBackupStatus, DatabaseRestoreResponse, StorageVisibilitySummary, Invoice, GRN, FgMovementCreate, FgMovementPrecheckResponse, FgSourceOption, CustomerStatementDetail, CustomerStatementSummaryResponse, SupplierStatementDetail, SupplierStatementSummaryRow, AllocationLine, ArOpenInvoice, ApOpenBill, ArReceiptRecord, ArReceiptSummary, ApPaymentRecord, ARReceiptMode, APPaymentMode, SupplierAgingSummaryRow, SupplierAgingDetailRow, ArPdcRegisterPayload, ApPdcRegisterPayload, PdcAllocationLine, PdcInstrumentDetail, PdcInstrumentSummary, PdcSide, PdcTransitionPayload, PdcTransitionResponse, ReportListResponse, EnquiryRegisterRow, ConversionRatioReport, SalesPipelineReport, QuotationRegisterRow, EstimateCostBreakdownRow, ProductionOutputResponse, MinStockAlertRow, ReceivablesAgingResponse, JobProductionCostRow, MaterialConsumptionRow, EstimatedVsActualRow, ContributionMarginRow, OwnerDashboardReport, ProfitAndLossReport, PagedReportResponse, PayablesAgingResponse, MaterialUomConversion, BalanceSheetResponse, TrialBalanceReportResponse, CashFlowReport, BreakEvenReport, GrcMaterialConsumptionRow, ProductProfitabilityRow, ReportReleaseReadinessResponse, ReportingConfig, ReportingReadinessResponse, ReportingReferenceData, WipReportResponse } from './types';
import { mockItems, mockCreateTrx, mockAccounts, mockCreateJournalHelper, mockBoms, mockWorkOrders, mockManufacturing, mockQuotes, mockReports, mockCreateWorkOrderFromQuote } from './mockData';
import type { VATAccountSettings } from './types';
import type {
    PostingCorrectionBatchSummary,
    PostingCorrectionBatchDetail,
    PostingCorrectionCandidate,
    PostingCorrectionDetail,
    PostingCorrectionPatch,
    PostingCorrectionPreview,
    PostingCorrectionSourceType,
} from './types';
import type { NavigationProfile } from '../types/module';
import { API_BASE_URL } from '../config/api';
import type { EmailPdfRequest, EmailPdfResponse, EstimateShareLinkRequest, EstimateShareLinkResponse } from './legacyApi';

const USE_MOCK = false;

function isLoopbackHost(hostname: string): boolean {
    const value = String(hostname || '').trim().toLowerCase();
    return value === 'localhost' || value === '127.0.0.1' || value === '::1';
}

// Helper for authorized requests
export async function authorizedFetch(url: string, options: RequestInit = {}) {
    let safeUrl = url;
    if (typeof window !== 'undefined') {
        try {
            const parsed = new URL(url, window.location.origin);
            const currentHost = window.location.hostname;
            const sameHost = parsed.hostname === currentHost
                || (isLoopbackHost(parsed.hostname) && isLoopbackHost(currentHost));
            if (sameHost) {
                if (window.location.protocol === 'https:' && parsed.protocol === 'http:') {
                    parsed.protocol = 'https:';
                }
                if (parsed.pathname.startsWith('/proxy/')) {
                    safeUrl = `${parsed.pathname}${parsed.search}${parsed.hash}`;
                } else if (!(parsed.pathname === '/api' || parsed.pathname.startsWith('/api/'))) {
                    parsed.pathname = `/api${parsed.pathname.startsWith('/') ? parsed.pathname : `/${parsed.pathname}`}`;
                    safeUrl = `${parsed.pathname}${parsed.search}${parsed.hash}`;
                } else {
                    safeUrl = `${parsed.pathname}${parsed.search}${parsed.hash}`;
                }
            }
        } catch {
            // keep original url if parsing fails
        }
    }
    return fetch(safeUrl, {
        ...options,
        credentials: 'include',
    });
}

async function buildApiError(response: Response, fallbackMessage: string): Promise<Error & { detail?: any }> {
    const payload = await response.json().catch(() => ({}));
    let detail = payload?.detail ?? payload;
    if (typeof detail === 'string' && Array.isArray(payload?.line_errors)) {
        detail = {
            message: detail,
            line_errors: payload.line_errors,
        };
    }
    if (typeof detail === 'string' && Array.isArray(payload?.row_errors)) {
        detail = {
            message: detail,
            row_errors: payload.row_errors,
        };
    }
    const message =
        typeof detail === 'string' ? detail :
            typeof detail?.message === 'string' ? detail.message :
                typeof payload?.message === 'string' ? payload.message :
                    fallbackMessage;
    const err = new Error(message) as Error & { detail?: any };
    err.detail = detail;
    return err;
}

function appendQueryParams(params: URLSearchParams, filters?: Record<string, string | number | undefined | null>) {
    Object.entries(filters || {}).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        params.set(key, String(value));
    });
}

async function downloadCsvFromApi(url: string, fallbackFilename: string): Promise<void> {
    const res = await authorizedFetch(url);
    if (!res.ok) {
        throw await buildApiError(res, 'Failed to export CSV');
    }
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename=([^;]+)/i);
    const filename = match ? match[1].replace(/["']/g, '') : fallbackFilename;
    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(objectUrl);
}

export interface DirectGrnResponse extends GRN {
    grn_id?: number;
    po_id?: number;
    auto_bill_created?: boolean;
    auto_bill_id?: number;
    auto_bill_status?: string;
    warning?: string;
}

export interface DirectGrnMaterialOption {
    material_id: number;
    material_name: string;
    item_type?: 'Stock' | 'Consumable' | 'Service' | 'FixedAsset' | string;
    unit?: string;
    cost_profiles?: string[];
    description?: string;
    base_unit_id?: number | null;
    base_unit_name?: string | null;
    uom_conversions?: MaterialUomConversion[];
}

export interface UsageVisitStartPayload {
    path: string;
    label?: string;
    source?: string;
}

export interface UsageVisitStartResponse {
    visit_id: number;
    started_at: string;
}

export interface UsageVisitUpdatePayload {
    ended?: boolean;
    exit_reason?: string;
    active_delta_seconds?: number;
}

export interface UsagePageSummaryRow {
    path: string;
    label: string;
    canonical_path?: string;
    visits: number;
    productive_visits?: number;
    zero_duration_visits?: number;
    superseded_visits?: number;
    total_duration_seconds: number;
    average_duration_seconds: number;
    capped_duration_seconds?: number;
    capped_average_duration_seconds?: number;
    active_duration_seconds?: number;
    active_average_duration_seconds?: number;
    active_median_duration_seconds?: number;
    active_p90_duration_seconds?: number;
    active_to_dwell_ratio?: number;
    signals?: string[];
    active_users: number;
    last_seen_at?: string | null;
}

export interface UsageAreaSummaryRow {
    canonical_path: string;
    label: string;
    area: string;
    visits: number;
    productive_visits: number;
    zero_duration_visits: number;
    superseded_visits: number;
    total_duration_seconds: number;
    average_duration_seconds: number;
    capped_duration_seconds: number;
    capped_average_duration_seconds: number;
    active_duration_seconds?: number;
    active_average_duration_seconds?: number;
    active_median_duration_seconds?: number;
    active_p90_duration_seconds?: number;
    active_to_dwell_ratio?: number;
    active_users: number;
    last_seen_at?: string | null;
}

export interface UsageUserSummaryRow {
    user_id: number;
    username?: string | null;
    full_name?: string | null;
    role?: string | null;
    visits: number;
    total_duration_seconds: number;
    average_duration_seconds: number;
    active_duration_seconds?: number;
    active_average_duration_seconds?: number;
}

export interface UsageAnalyticsConfig {
    enabled: boolean;
}

export interface UsageAnalyticsSummary {
    period_days: number;
    since: string;
    generated_at: string;
    totals: {
        visits: number;
        active_users: number;
        total_duration_seconds: number;
        average_duration_seconds: number;
        capped_duration_seconds?: number;
        capped_average_duration_seconds?: number;
        active_duration_seconds?: number;
        active_average_duration_seconds?: number;
        productive_visits?: number;
    };
    data_quality?: {
        duration_cap_seconds: number;
        zero_duration_visits: number;
        superseded_visits: number;
        long_duration_visits: number;
    };
    top_areas?: UsageAreaSummaryRow[];
    top_pages_by_time: UsagePageSummaryRow[];
    top_pages_by_visits: UsagePageSummaryRow[];
    improvement_targets?: UsagePageSummaryRow[];
    top_users: UsageUserSummaryRow[];
}

export const api = {
    estimates: {
        update: async (id: number, payload: any) => {
            const res = await authorizedFetch(`${API_BASE_URL}/estimates/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to update estimate');
            }
            return res.json();
        },
    },
    invoices: {
        createDirect: async (payload: any): Promise<Invoice> => {
            const res = await authorizedFetch(`${API_BASE_URL}/sales/invoices/direct`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) throw new Error('Failed to create direct invoice');
            return res.json();
        },
    },
    purchasing: {
        listDirectGrnMaterialOptions: async (): Promise<DirectGrnMaterialOption[]> => {
            const res = await authorizedFetch(`${API_BASE_URL}/purchasing/grn/options/materials`);
            if (!res.ok) throw await buildApiError(res, 'Failed to load materials');
            return res.json();
        },
        createDirectGRN: async (payload: any): Promise<DirectGrnResponse> => {
            const res = await authorizedFetch(`${API_BASE_URL}/purchasing/grn/direct`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) throw await buildApiError(res, 'Failed to create direct GRN');
            return res.json();
        },
    },
    inventory: {
        list: async (options?: { role?: string }): Promise<Item[]> => {
            if (USE_MOCK) return Promise.resolve(mockItems);
            const roleQuery = options?.role ? `?role=${encodeURIComponent(options.role)}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/inventory/items${roleQuery}`);
            return res.json();
        },
        createTransaction: async (txn: StockTransactionCreate) => {
            if (USE_MOCK) return Promise.resolve(mockCreateTrx(txn));
            const res = await authorizedFetch(`${API_BASE_URL}/inventory/transactions`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(txn),
            });
            return res.json();
        },
        getLedger: async (itemId?: number): Promise<StockMove[]> => {
            const url = itemId ? `${API_BASE_URL}/inventory/ledger?item_id=${itemId}` : `${API_BASE_URL}/inventory/ledger`;
            const res = await authorizedFetch(url);
            return res.json();
        },
        createAdjustment: async (adj: StockAdjustmentCreate) => {
            const res = await authorizedFetch(`${API_BASE_URL}/inventory/adjustments`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(adj),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to create stock adjustment');
            }
            return res.json();
        },
        createInternalReceipt: async (payload: InternalReceiptCreate) => {
            const res = await authorizedFetch(`${API_BASE_URL}/inventory/internal-receipts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to create internal stock update');
            }
            return res.json();
        },
        precheckInternalReceipt: async (payload: InventoryPrecheckRequest): Promise<InventoryPrecheckResponse> => {
            const res = await authorizedFetch(`${API_BASE_URL}/inventory/precheck/internal-receipt`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to precheck internal stock update');
            }
            return res.json();
        },
        precheckAdjustment: async (payload: InventoryPrecheckRequest): Promise<InventoryPrecheckResponse> => {
            const res = await authorizedFetch(`${API_BASE_URL}/inventory/precheck/adjustment`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to precheck stock adjustment');
            }
            return res.json();
        },
        getFgSourceOptions: async (itemId: number, mode: string): Promise<FgSourceOption[]> => {
            const res = await authorizedFetch(`${API_BASE_URL}/inventory/fg/source-options?item_id=${encodeURIComponent(itemId)}&mode=${encodeURIComponent(mode)}`);
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to load FG source options');
            }
            const payload = await res.json();
            return (payload.options || []) as FgSourceOption[];
        },
        precheckFgMovement: async (payload: FgMovementCreate): Promise<FgMovementPrecheckResponse> => {
            const res = await authorizedFetch(`${API_BASE_URL}/inventory/fg/movements/precheck`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to precheck FG movement');
            }
            return res.json();
        },
        createFgMovement: async (payload: FgMovementCreate) => {
            const res = await authorizedFetch(`${API_BASE_URL}/inventory/fg/movements`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to create FG movement');
            }
            return res.json();
        },
    },
    financials: {
        listAccounts: async (): Promise<Account[]> => {
            if (USE_MOCK) return Promise.resolve(mockAccounts);
            const res = await authorizedFetch(`${API_BASE_URL}/financials/accounts`);
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to load accounts');
            }
            return res.json();
        },
        getVATAccountSettings: async (): Promise<VATAccountSettings> => {
            const res = await authorizedFetch(`${API_BASE_URL}/financials/vat-settings`);
            if (!res.ok) throw await buildApiError(res, 'Failed to load VAT account mappings');
            return res.json();
        },
        updateVATAccountSettings: async (payload: VATAccountSettings): Promise<VATAccountSettings> => {
            const res = await authorizedFetch(`${API_BASE_URL}/financials/vat-settings`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw await buildApiError(res, 'Failed to update VAT account mappings');
            return res.json();
        },
        getAccount: async (accountId: number): Promise<Account> => {
            const res = await authorizedFetch(`${API_BASE_URL}/financials/accounts/${accountId}`);
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to load account');
            }
            return res.json();
        },
        createAccount: async (payload: AccountUpsertPayload): Promise<Account> => {
            const res = await authorizedFetch(`${API_BASE_URL}/financials/accounts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to create account');
            }
            return res.json();
        },
        updateAccount: async (accountId: number, payload: AccountUpsertPayload): Promise<Account> => {
            const res = await authorizedFetch(`${API_BASE_URL}/financials/accounts/${accountId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to update account');
            }
            return res.json();
        },
        getAccountLedger: async (
            accountId: number,
            filters?: { from?: string; to?: string; limit?: number; offset?: number }
        ): Promise<AccountLedgerResponse> => {
            const params = new URLSearchParams();
            appendQueryParams(params, filters);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/financials/accounts/${accountId}/ledger${suffix}`);
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to load account ledger');
            }
            return res.json();
        },
        getAccountRemapPreview: async (
            accountId: number,
            filters?: { from?: string; to?: string; limit?: number; offset?: number }
        ): Promise<AccountRemapPreview> => {
            const params = new URLSearchParams();
            appendQueryParams(params, filters);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/financials/accounts/${accountId}/remap-preview${suffix}`);
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to load remap preview');
            }
            return res.json();
        },
        remapAccountLines: async (
            accountId: number,
            payload: { target_account_id: number; line_ids: number[]; reason: string }
        ): Promise<AccountRemapResult> => {
            const res = await authorizedFetch(`${API_BASE_URL}/financials/accounts/${accountId}/remap-lines`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to remap account lines');
            }
            return res.json();
        },
        createJournal: async (entry: JournalEntryCreate) => {
            if (USE_MOCK) return Promise.resolve(mockCreateJournalHelper(entry));
            const res = await authorizedFetch(`${API_BASE_URL}/financials/journal-entries`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(entry),
            });
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to post journal entry');
            }
            return res.json();
        },
        listFiscalPeriods: async (): Promise<FiscalPeriod[]> => {
            const res = await authorizedFetch(`${API_BASE_URL}/accounting/periods`);
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to load fiscal periods');
            }
            return res.json();
        },
        createFiscalPeriod: async (payload: FiscalPeriodCreate): Promise<FiscalPeriod> => {
            const res = await authorizedFetch(`${API_BASE_URL}/accounting/periods`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to create fiscal period');
            }
            return res.json();
        },
        updateFiscalPeriodStatus: async (periodId: number, status: FiscalPeriodStatus): Promise<{ status: string; message: string }> => {
            const res = await authorizedFetch(`${API_BASE_URL}/accounting/periods/${periodId}/status`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status }),
            });
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to update fiscal period status');
            }
            return res.json();
        },
        listCustomerStatementsSummary: async (filters?: { from?: string; to?: string; q?: string; limit?: number; offset?: number }): Promise<CustomerStatementSummaryResponse> => {
            const params = new URLSearchParams();
            if (filters?.from) params.set('from', filters.from);
            if (filters?.to) params.set('to', filters.to);
            if (filters?.q) params.set('q', filters.q);
            if (typeof filters?.limit === 'number') params.set('limit', String(filters.limit));
            if (typeof filters?.offset === 'number') params.set('offset', String(filters.offset));
            const suffix = params.toString() ? `?${params.toString()}` : '';
            let res = await authorizedFetch(`${API_BASE_URL}/financials/customer-statements/summary${suffix}`);
            if (res.status === 404) {
                // Backward-compatible fallback for environments where financials router
                // is not mounted but finance sales routes are available.
                res = await authorizedFetch(`${API_BASE_URL}/sales/customer-statements/summary${suffix}`);
            }
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to fetch customer statement summary');
            }
            return res.json();
        },
        exportCustomerOutstandingReportCsv: async (filters?: { from?: string; to?: string; q?: string }): Promise<void> => {
            const params = new URLSearchParams();
            if (filters?.from) params.set('from', filters.from);
            if (filters?.to) params.set('to', filters.to);
            if (filters?.q) params.set('q', filters.q);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            let res = await authorizedFetch(`${API_BASE_URL}/financials/customer-statements/outstanding/export.csv${suffix}`);
            if (res.status === 404) {
                res = await authorizedFetch(`${API_BASE_URL}/sales/customer-statements/outstanding/export.csv${suffix}`);
            }
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to export customer outstanding report CSV');
            }
            const blob = await res.blob();
            const disposition = res.headers.get('Content-Disposition') || '';
            const match = disposition.match(/filename=([^;]+)/i);
            const filename = match ? match[1].replace(/["']/g, '') : `customer_outstanding_report.csv`;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        },
        getCustomerStatement: async (customerId: number, filters?: { from?: string; to?: string }): Promise<CustomerStatementDetail> => {
            const params = new URLSearchParams();
            if (filters?.from) params.set('from', filters.from);
            if (filters?.to) params.set('to', filters.to);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            let res = await authorizedFetch(`${API_BASE_URL}/financials/customer-statements/${customerId}${suffix}`);
            if (res.status === 404) {
                res = await authorizedFetch(`${API_BASE_URL}/sales/customer-statements/${customerId}${suffix}`);
            }
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to fetch customer statement detail');
            }
            return res.json();
        },
        exportCustomerStatementCsv: async (customerId: number, filters?: { from?: string; to?: string }): Promise<void> => {
            const params = new URLSearchParams();
            if (filters?.from) params.set('from', filters.from);
            if (filters?.to) params.set('to', filters.to);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            let res = await authorizedFetch(`${API_BASE_URL}/financials/customer-statements/${customerId}/export.csv${suffix}`);
            if (res.status === 404) {
                res = await authorizedFetch(`${API_BASE_URL}/sales/customer-statements/${customerId}/export.csv${suffix}`);
            }
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to export customer statement CSV');
            }
            const blob = await res.blob();
            const disposition = res.headers.get('Content-Disposition') || '';
            const match = disposition.match(/filename=([^;]+)/i);
            const filename = match ? match[1].replace(/["']/g, '') : `customer_statement_${customerId}.csv`;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        },
        openCustomerStatementPrint: (customerId: number, filters?: { from?: string; to?: string }): void => {
            const params = new URLSearchParams();
            if (filters?.from) params.set('from', filters.from);
            if (filters?.to) params.set('to', filters.to);
            params.set('autoprint', '1');
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const printUrl = `${API_BASE_URL}/financials/customer-statements/${customerId}/print${suffix}`;
            const printWindow = window.open(printUrl, '_blank');
            if (!printWindow) {
                throw new Error('Pop-up blocked. Please allow pop-ups and try again.');
            }
            printWindow.focus();
        },
        openCustomerOutstandingPrint: (filters?: { from?: string; to?: string; q?: string }): void => {
            const params = new URLSearchParams();
            if (filters?.from) params.set('from', filters.from);
            if (filters?.to) params.set('to', filters.to);
            if (filters?.q) params.set('q', filters.q);
            params.set('autoprint', '1');
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const printUrl = `${API_BASE_URL}/financials/customer-statements/outstanding/print${suffix}`;
            const printWindow = window.open(printUrl, '_blank');
            if (!printWindow) {
                throw new Error('Pop-up blocked. Please allow pop-ups and try again.');
            }
            printWindow.focus();
        },
        listSupplierStatementsSummary: async (filters?: { from?: string; to?: string; q?: string; limit?: number; offset?: number }): Promise<{ total: number; items: SupplierStatementSummaryRow[] }> => {
            const params = new URLSearchParams();
            if (filters?.from) params.set('from', filters.from);
            if (filters?.to) params.set('to', filters.to);
            if (filters?.q) params.set('q', filters.q);
            if (typeof filters?.limit === 'number') params.set('limit', String(filters.limit));
            if (typeof filters?.offset === 'number') params.set('offset', String(filters.offset));
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/financials/supplier-statements/summary${suffix}`);
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to fetch supplier statement summary');
            }
            return res.json();
        },
        getSupplierStatement: async (supplierId: number, filters?: { from?: string; to?: string }): Promise<SupplierStatementDetail> => {
            const params = new URLSearchParams();
            if (filters?.from) params.set('from', filters.from);
            if (filters?.to) params.set('to', filters.to);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/financials/supplier-statements/${supplierId}${suffix}`);
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to fetch supplier statement detail');
            }
            return res.json();
        },
        exportSupplierStatementCsv: async (supplierId: number, filters?: { from?: string; to?: string }): Promise<void> => {
            const params = new URLSearchParams();
            if (filters?.from) params.set('from', filters.from);
            if (filters?.to) params.set('to', filters.to);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/financials/supplier-statements/${supplierId}/export.csv${suffix}`);
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to export supplier statement CSV');
            }
            const blob = await res.blob();
            const disposition = res.headers.get('Content-Disposition') || '';
            const match = disposition.match(/filename=([^;]+)/i);
            const filename = match ? match[1].replace(/["']/g, '') : `supplier_statement_${supplierId}.csv`;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        },
        listArOpenInvoices: async (customerId: number): Promise<ArOpenInvoice[]> => {
            const res = await authorizedFetch(`${API_BASE_URL}/financials/ar/open-invoices?customer_id=${customerId}`);
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to fetch open invoices');
            }
            const payload = await res.json();
            return (payload?.items || []) as ArOpenInvoice[];
        },
        postArReceipt: async (payload: {
            customer_id: number;
            mode: ARReceiptMode | string;
            amount: number;
            date?: string;
            reference?: string;
            cash_account_id?: number;
            so_id?: number;
            invoice_hint_id?: number;
            allocations?: AllocationLine[];
        }): Promise<ArReceiptRecord> => {
            const res = await authorizedFetch(`${API_BASE_URL}/financials/ar/receipts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to post customer receipt');
            }
            return res.json();
        },
        allocateArReceipt: async (receiptId: number, allocations: AllocationLine[]): Promise<ArReceiptRecord> => {
            const res = await authorizedFetch(`${API_BASE_URL}/financials/ar/receipts/${receiptId}/allocations`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ allocations }),
            });
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to allocate receipt');
            }
            return res.json();
        },
        getArReceipt: async (receiptId: number): Promise<ArReceiptRecord> => {
            const res = await authorizedFetch(`${API_BASE_URL}/financials/ar/receipts/${receiptId}`);
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to fetch receipt details');
            }
            return res.json();
        },
        listArReceipts: async (filters?: {
            customer_id?: number;
            mode?: string;
            q?: string;
            limit?: number;
            offset?: number;
        }): Promise<{ total: number; limit: number; offset: number; has_more: boolean; items: ArReceiptSummary[] }> => {
            const params = new URLSearchParams();
            if (typeof filters?.customer_id === 'number' && filters.customer_id > 0) params.set('customer_id', String(filters.customer_id));
            if (filters?.mode) params.set('mode', filters.mode);
            if (filters?.q) params.set('q', filters.q);
            if (typeof filters?.limit === 'number') params.set('limit', String(filters.limit));
            if (typeof filters?.offset === 'number') params.set('offset', String(filters.offset));
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/financials/ar/receipts${suffix}`);
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to fetch receipt register');
            }
            return res.json();
        },
        openArReceiptPrint: (receiptId: number, autoprint = true): void => {
            const params = new URLSearchParams();
            if (autoprint) params.set('autoprint', '1');
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const printUrl = `${API_BASE_URL}/financials/ar/receipts/${receiptId}/print${suffix}`;
            const printWindow = window.open(printUrl, '_blank');
            if (!printWindow) {
                throw new Error('Pop-up blocked. Please allow pop-ups and try again.');
            }
            printWindow.focus();
        },
        downloadArReceiptPdf: async (receiptId: number): Promise<void> => {
            const printUrl = `${API_BASE_URL}/financials/ar/receipts/${receiptId}/print?autoprint=1`;
            const printWindow = window.open(printUrl, '_blank');
            if (!printWindow) {
                throw new Error('Pop-up blocked. Please allow pop-ups and try again.');
            }
            printWindow.focus();
        },
        emailArReceiptPdf: async (receiptId: number, payload: EmailPdfRequest): Promise<EmailPdfResponse> => {
            const res = await authorizedFetch(`${API_BASE_URL}/financials/ar/receipts/${receiptId}/email-pdf`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to send receipt PDF');
            }
            return res.json();
        },
        createArReceiptShareLink: async (receiptId: number, payload?: EstimateShareLinkRequest): Promise<EstimateShareLinkResponse> => {
            const res = await authorizedFetch(`${API_BASE_URL}/financials/ar/receipts/${receiptId}/share-link`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload || {}),
            });
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to create receipt share link');
            }
            return res.json();
        },
        revokeArReceiptShareLink: async (receiptId: number): Promise<void> => {
            const res = await authorizedFetch(`${API_BASE_URL}/financials/ar/receipts/${receiptId}/share-link/revoke`, {
                method: 'POST',
            });
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to revoke receipt share link');
            }
        },
        listApOpenBills: async (supplierId: number): Promise<ApOpenBill[]> => {
            const res = await authorizedFetch(`${API_BASE_URL}/financials/ap/open-bills?supplier_id=${supplierId}`);
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to fetch open supplier bills');
            }
            const payload = await res.json();
            return (payload?.items || []) as ApOpenBill[];
        },
        postApPayment: async (payload: {
            supplier_id: number;
            mode: APPaymentMode | string;
            amount: number;
            date?: string;
            reference?: string;
            cash_account_id?: number;
            bill_hint_id?: number;
            allocations?: AllocationLine[];
        }): Promise<ApPaymentRecord> => {
            const res = await authorizedFetch(`${API_BASE_URL}/financials/ap/payments`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to post supplier payment');
            }
            return res.json();
        },
        allocateApPayment: async (paymentId: number, allocations: AllocationLine[]): Promise<ApPaymentRecord> => {
            const res = await authorizedFetch(`${API_BASE_URL}/financials/ap/payments/${paymentId}/allocations`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ allocations }),
            });
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to allocate supplier payment');
            }
            return res.json();
        },
        getApPayment: async (paymentId: number): Promise<ApPaymentRecord> => {
            const res = await authorizedFetch(`${API_BASE_URL}/financials/ap/payments/${paymentId}`);
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to fetch payment details');
            }
            return res.json();
        },
        listApAgingSummary: async (asOf?: string): Promise<{ as_of: string; total: number; items: SupplierAgingSummaryRow[] }> => {
            const params = new URLSearchParams();
            if (asOf) params.set('as_of', asOf);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/financials/ap/aging/summary${suffix}`);
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to fetch AP aging summary');
            }
            return res.json();
        },
        getApAgingDetail: async (supplierId: number, asOf?: string): Promise<{ as_of: string; supplier_id: number; supplier_name: string; totals: Record<string, number>; items: SupplierAgingDetailRow[] }> => {
            const params = new URLSearchParams();
            if (asOf) params.set('as_of', asOf);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/financials/ap/aging/${supplierId}${suffix}`);
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to fetch AP aging detail');
            }
            return res.json();
        },
        registerArPdc: async (payload: ArPdcRegisterPayload): Promise<PdcInstrumentDetail> => {
            const res = await authorizedFetch(`${API_BASE_URL}/financials/pdc/ar/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to register AR PDC');
            }
            return res.json();
        },
        registerApPdc: async (payload: ApPdcRegisterPayload): Promise<PdcInstrumentDetail> => {
            const res = await authorizedFetch(`${API_BASE_URL}/financials/pdc/ap/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to register AP PDC');
            }
            return res.json();
        },
        listPdc: async (filters?: {
            side?: PdcSide | string;
            status_id?: number;
            party_id?: number;
            due_from?: string;
            due_to?: string;
            include_closed?: boolean;
        }): Promise<{ items: PdcInstrumentSummary[] }> => {
            const params = new URLSearchParams();
            if (filters?.side) params.set('side', String(filters.side));
            if (typeof filters?.status_id === 'number' && filters.status_id > 0) params.set('status_id', String(filters.status_id));
            if (typeof filters?.party_id === 'number' && filters.party_id > 0) params.set('party_id', String(filters.party_id));
            if (filters?.due_from) params.set('due_from', filters.due_from);
            if (filters?.due_to) params.set('due_to', filters.due_to);
            if (typeof filters?.include_closed === 'boolean') params.set('include_closed', filters.include_closed ? 'true' : 'false');
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/financials/pdc${suffix}`);
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to fetch PDC register');
            }
            return res.json();
        },
        getPdc: async (pdcId: number): Promise<PdcInstrumentDetail> => {
            const res = await authorizedFetch(`${API_BASE_URL}/financials/pdc/${pdcId}`);
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to fetch PDC details');
            }
            return res.json();
        },
        updatePdcAllocations: async (pdcId: number, allocations: PdcAllocationLine[]): Promise<PdcInstrumentDetail> => {
            const res = await authorizedFetch(`${API_BASE_URL}/financials/pdc/${pdcId}/allocations`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ allocations }),
            });
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to update PDC allocations');
            }
            return res.json();
        },
        transitionPdc: async (payload: PdcTransitionPayload): Promise<PdcTransitionResponse> => {
            const { pdc_id, ...body } = payload;
            const res = await authorizedFetch(`${API_BASE_URL}/financials/pdc/${pdc_id}/transition`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to process PDC transition');
            }
            return res.json();
        },
        listPostingCorrectionCandidates: async (filters?: {
            source_type?: string;
            from?: string;
            to?: string;
            bank_account_id?: number;
            q?: string;
            limit?: number;
            offset?: number;
        }): Promise<{ total: number; limit: number; offset: number; has_more: boolean; items: PostingCorrectionCandidate[] }> => {
            const params = new URLSearchParams();
            appendQueryParams(params, filters);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/financials/posting-corrections/candidates${suffix}`);
            if (!res.ok) throw await buildApiError(res, 'Failed to load posting correction candidates');
            return res.json();
        },
        getPostingCorrectionDetail: async (sourceType: PostingCorrectionSourceType, sourceId: number): Promise<PostingCorrectionDetail> => {
            const res = await authorizedFetch(`${API_BASE_URL}/financials/posting-corrections/candidates/${sourceType}/${sourceId}`);
            if (!res.ok) throw await buildApiError(res, 'Failed to load posting correction detail');
            return res.json();
        },
        previewPostingCorrections: async (payload: {
            reason: string;
            items: Array<{ source_type: PostingCorrectionSourceType; source_id: number; changes: PostingCorrectionPatch }>;
        }): Promise<PostingCorrectionPreview> => {
            const res = await authorizedFetch(`${API_BASE_URL}/financials/posting-corrections/batches/preview`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw await buildApiError(res, 'Failed to preview posting corrections');
            return res.json();
        },
        applyPostingCorrectionBatch: async (batchId: number, confirmation: string): Promise<Record<string, any>> => {
            const res = await authorizedFetch(`${API_BASE_URL}/financials/posting-corrections/batches/${batchId}/apply`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ confirmation }),
            });
            if (!res.ok) throw await buildApiError(res, 'Failed to apply posting correction batch');
            return res.json();
        },
        listPostingCorrectionBatches: async (): Promise<{ items: PostingCorrectionBatchSummary[] }> => {
            const res = await authorizedFetch(`${API_BASE_URL}/financials/posting-corrections/batches`);
            if (!res.ok) throw await buildApiError(res, 'Failed to load posting correction history');
            return res.json();
        },
        getPostingCorrectionBatch: async (batchId: number): Promise<PostingCorrectionBatchDetail> => {
            const res = await authorizedFetch(`${API_BASE_URL}/financials/posting-corrections/batches/${batchId}`);
            if (!res.ok) throw await buildApiError(res, 'Failed to load posting correction evidence');
            return res.json();
        },
        undoPostingCorrectionBatch: async (batchId: number): Promise<Record<string, any>> => {
            const res = await authorizedFetch(`${API_BASE_URL}/financials/posting-corrections/batches/${batchId}/undo`, { method: 'POST' });
            if (!res.ok) throw await buildApiError(res, 'Failed to undo posting correction batch');
            return res.json();
        },
        exportPostingCorrectionCsv: async (): Promise<void> => {
            await downloadCsvFromApi(`${API_BASE_URL}/financials/posting-corrections/export.csv`, 'posting_corrections.csv');
        },
        previewPostingCorrectionCsv: async (reason: string, csvText: string): Promise<PostingCorrectionPreview> => {
            const res = await authorizedFetch(`${API_BASE_URL}/financials/posting-corrections/csv-preview`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ reason, csv_text: csvText }),
            });
            if (!res.ok) throw await buildApiError(res, 'Failed to preview posting correction CSV');
            return res.json();
        },
    },
    manufacturing: {
        listBoms: async (): Promise<BOM[]> => {
            if (USE_MOCK) return Promise.resolve(mockBoms);
            const res = await authorizedFetch(`${API_BASE_URL}/manufacturing/boms`);
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to fetch BOMs');
            }
            return res.json();
        },
        createBom: async (bom: BOMCreate) => {
            if (USE_MOCK) return Promise.resolve(mockManufacturing.createBom(bom));
            const res = await authorizedFetch(`${API_BASE_URL}/manufacturing/boms`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(bom),
            });
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to save BOM');
            }
            return res.json();
        },
        getBom: async (id: number): Promise<BOM> => {
            const res = await authorizedFetch(`${API_BASE_URL}/manufacturing/boms/${id}`);
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to fetch standard recipe');
            }
            return res.json();
        },
        updateBom: async (id: number, bom: BOMCreate): Promise<BOM> => {
            const res = await authorizedFetch(`${API_BASE_URL}/manufacturing/boms/${id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(bom),
            });
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to save standard recipe revision');
            }
            return res.json();
        },
        listWorkOrders: async (): Promise<WorkOrder[]> => {
            if (USE_MOCK) return Promise.resolve(mockWorkOrders);
            const res = await authorizedFetch(`${API_BASE_URL}/manufacturing/work-orders`);
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to fetch work orders');
            }
            return res.json();
        },
        createWorkOrder: async (wo: WorkOrderCreate) => {
            if (USE_MOCK) return Promise.resolve(mockManufacturing.createWorkOrder(wo));
            const res = await authorizedFetch(`${API_BASE_URL}/manufacturing/work-orders`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(wo),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to create work order');
            }
            return res.json();
        },
        getWorkOrder: async (id: number): Promise<WorkOrder> => {
            if (USE_MOCK) return Promise.resolve(mockWorkOrders.find(w => w.wo_id === id) as WorkOrder);
            const res = await authorizedFetch(`${API_BASE_URL}/manufacturing/work-orders/${id}`);
            return res.json();
        },
        getWorkOrderRequirements: async (id: number): Promise<WorkOrderRequirementsResponse> => {
            const res = await authorizedFetch(`${API_BASE_URL}/manufacturing/work-orders/${id}/requirements`);
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to load work-order material requirements');
            }
            return res.json();
        },
        getWorkOrderMaterials: async (id: number): Promise<WorkOrderMaterialsResponse> => {
            const res = await authorizedFetch(`${API_BASE_URL}/manufacturing/work-orders/${id}/materials`);
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to load work-order materials');
            }
            return res.json();
        },
        getMaterialIssueBoard: async (): Promise<WorkOrderMaterialIssueBoardResponse> => {
            const res = await authorizedFetch(`${API_BASE_URL}/manufacturing/material-issue-board`);
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to load material issue board');
            }
            return res.json();
        },
        postMaterialIssues: async (payload: WorkOrderMaterialMovementRequest): Promise<WorkOrderMaterialMovementResponse> => {
            const res = await authorizedFetch(`${API_BASE_URL}/manufacturing/material-issues`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to issue material to work order');
            }
            return res.json();
        },
        postMaterialReturns: async (payload: WorkOrderMaterialMovementRequest): Promise<WorkOrderMaterialMovementResponse> => {
            const res = await authorizedFetch(`${API_BASE_URL}/manufacturing/material-returns`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to return material to store');
            }
            return res.json();
        },
        addUnplannedMaterial: async (id: number, payload: WorkOrderUnplannedMaterialCreate) => {
            const res = await authorizedFetch(`${API_BASE_URL}/manufacturing/work-orders/${id}/materials/unplanned`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to add unplanned material');
            }
            return res.json();
        },
        createWorkOrderItem: async (id: number, payload: WorkOrderSubPartCreate): Promise<WorkOrderItem> => {
            const res = await authorizedFetch(`${API_BASE_URL}/manufacturing/work-orders/${id}/items`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to create work-order sub-part');
            }
            return res.json();
        },
        updateWorkOrderItem: async (id: number, woItemId: number, payload: WorkOrderSubPartUpdate): Promise<WorkOrderItem> => {
            const res = await authorizedFetch(`${API_BASE_URL}/manufacturing/work-orders/${id}/items/${woItemId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to update work-order sub-part');
            }
            return res.json();
        },
        updateWorkOrderItemMetadata: async (id: number, woItemId: number, payload: WorkOrderItemMetadataUpdate): Promise<WorkOrderItem> => {
            const res = await authorizedFetch(`${API_BASE_URL}/manufacturing/work-orders/${id}/items/${woItemId}/metadata`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to update work-order line details');
            }
            return res.json();
        },
        applyWorkOrderStandardRecipe: async (
            id: number,
            woItemId: number,
            payload: { standard_recipe_bom_id?: number | null; create_sub_parts?: boolean },
        ): Promise<{ wo_id: number; wo_item_id: number; standard_recipe_bom_id?: number | null; created_sub_parts: WorkOrderItem[] }> => {
            const res = await authorizedFetch(`${API_BASE_URL}/manufacturing/work-orders/${id}/items/${woItemId}/standard-recipe`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to apply standard recipe');
            }
            return res.json();
        },
        deleteWorkOrderItem: async (id: number, woItemId: number): Promise<{ status: string; wo_item_id: number }> => {
            const res = await authorizedFetch(`${API_BASE_URL}/manufacturing/work-orders/${id}/items/${woItemId}`, {
                method: 'DELETE',
            });
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to delete work-order sub-part');
            }
            return res.json();
        },
        postShiftEntry: async (payload: WorkOrderShiftEntryRequest): Promise<WorkOrderShiftEntryResponse> => {
            const res = await authorizedFetch(`${API_BASE_URL}/manufacturing/shift-entries`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to post shift entry');
            }
            return res.json();
        },
        postWorkOrderDailyEntry: async (id: number, payload: WorkOrderDailyEntryRequest): Promise<WorkOrderDailyEntryResponse> => {
            const res = await authorizedFetch(`${API_BASE_URL}/manufacturing/work-orders/${id}/daily-entry`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to post daily entry');
            }
            return res.json();
        },
        reverseWorkOrderDailyEntry: async (id: number, entryId: number, reason?: string): Promise<WorkOrderDailyEntryResponse & { reversed_entry_id?: number }> => {
            const res = await authorizedFetch(`${API_BASE_URL}/manufacturing/work-orders/${id}/daily-entry/${entryId}/reverse`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ reason }),
            });
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to reverse daily entry');
            }
            return res.json();
        },
        listWorkOrderDailyEntries: async (id: number): Promise<WorkOrderDailyEntriesListResponse> => {
            const res = await authorizedFetch(`${API_BASE_URL}/manufacturing/work-orders/${id}/daily-entry`);
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to load daily entries');
            }
            return res.json();
        },
        openWorkOrderPrint: (id: number, autoprint = true): void => {
            const params = new URLSearchParams();
            if (autoprint) params.set('autoprint', '1');
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const printUrl = `${API_BASE_URL}/manufacturing/work-orders/${id}/print${suffix}`;
            const printWindow = window.open(printUrl, '_blank');
            if (!printWindow) {
                throw new Error('Pop-up blocked. Please allow pop-ups and try again.');
            }
            printWindow.focus();
        },
        startWorkOrder: async (id: number) => {
            if (USE_MOCK) return Promise.resolve(mockManufacturing.startWorkOrder(id));
            const res = await authorizedFetch(`${API_BASE_URL}/manufacturing/work-orders/${id}/start`, { method: 'POST' });
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to start work order');
            }
            return res.json();
        },
        postProductionEntry: async (id: number, payload: WorkOrderProductionEntryRequest): Promise<WorkOrderProductionEntryResponse> => {
            const res = await authorizedFetch(`${API_BASE_URL}/manufacturing/work-orders/${id}/production-entry`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to post production entry');
            }
            return res.json();
        },
        completeWorkOrder: async (id: number, costs?: { labor_cost: number; overhead_cost: number }) => {
            if (USE_MOCK) return Promise.resolve(mockManufacturing.completeWorkOrder(id));
            const res = await authorizedFetch(`${API_BASE_URL}/manufacturing/work-orders/${id}/complete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(costs || {})
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to complete work order');
            }
            return res.json();
        },
        deleteWorkOrder: async (id: number) => {
            const res = await authorizedFetch(`${API_BASE_URL}/manufacturing/work-orders/${id}`, {
                method: 'DELETE',
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to delete work order');
            }
            return res.json();
        },
        createWorkOrderFromQuote: async (quoteId: string) => {
            if (USE_MOCK) return Promise.resolve(mockCreateWorkOrderFromQuote(quoteId));
            const res = await authorizedFetch(`${API_BASE_URL}/manufacturing/work-orders/from-quote/${quoteId}`, { method: 'POST' });
            return res.json();
        }
    },
    reports: {
        getStockValuation: async (): Promise<StockValuationRecord[]> => {
            if (USE_MOCK) return Promise.resolve(mockReports.getStockValuation());
            const res = await authorizedFetch(`${API_BASE_URL}/reports/stock-valuation`);
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to fetch stock valuation');
            }
            return res.json();
        },
        getJobProfitability: async (): Promise<JobProfitabilityRecord[]> => {
            if (USE_MOCK) return Promise.resolve(mockReports.getJobProfitability());
            const res = await authorizedFetch(`${API_BASE_URL}/reports/job-profitability`);
            if (!res.ok) {
                throw await buildApiError(res, 'Failed to fetch job profitability');
            }
            return res.json();
        },
        getVATSummary: async (filters?: { from?: string; to?: string; q?: string; limit?: number; offset?: number }): Promise<VATSummary> => {
            const params = new URLSearchParams();
            appendQueryParams(params, filters);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/reports/vat-summary${suffix}`);
            if (!res.ok) throw await buildApiError(res, 'Failed to fetch VAT summary');
            return res.json();
        },
        bulkReviewVatRecovery: async (payload: {
            bill_ids: number[];
            recovery_percent: number;
            vat_treatment: string;
            nonrecoverable_vat_account_id?: number | null;
        }): Promise<{ updated: number; items: Array<Record<string, unknown>> }> => {
            const res = await authorizedFetch(`${API_BASE_URL}/purchasing/bills/vat-recovery/bulk`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw await buildApiError(res, 'Failed to review Input VAT recovery');
            return res.json();
        },
        updateVatSupplierTrn: async (supplierId: number, trn: string): Promise<{ supplier_id: number; supplier_name: string; trn: string }> => {
            const res = await authorizedFetch(`${API_BASE_URL}/reports/vat-summary/suppliers/${supplierId}/trn`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ trn }),
            });
            if (!res.ok) throw await buildApiError(res, 'Failed to update supplier TRN');
            return res.json();
        },
        updateVatCustomerEmirate: async (customerId: number, emirate: string): Promise<{ customer_id: number; customer_name: string; emirate: string }> => {
            const res = await authorizedFetch(`${API_BASE_URL}/reports/vat-summary/customers/${customerId}/emirate`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ emirate }),
            });
            if (!res.ok) throw await buildApiError(res, 'Failed to update customer emirate');
            return res.json();
        },
        getDashboardSummary: async (): Promise<DashboardSummary> => {
            const res = await authorizedFetch(`${API_BASE_URL}/reports/dashboard`);
            return res.json();
        },
        getExecutiveDashboard: async (filters?: { year?: number; compare_year?: number }): Promise<ExecutiveDashboardResponse> => {
            const params = new URLSearchParams();
            appendQueryParams(params, filters);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/reports/executive-dashboard${suffix}`);
            if (!res.ok) throw await buildApiError(res, 'Failed to fetch executive dashboard');
            return res.json();
        },
        getExecutiveModuleDashboard: async (moduleKey: string, filters?: { year?: number; compare_year?: number; period_mode?: 'ytd' | 'month' }): Promise<ExecutiveModuleDashboardResponse> => {
            const params = new URLSearchParams();
            appendQueryParams(params, filters);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/reports/executive-dashboard/${encodeURIComponent(moduleKey)}${suffix}`);
            if (!res.ok) throw await buildApiError(res, `Failed to fetch ${moduleKey} executive dashboard`);
            return res.json();
        },
        getEnquiryRegister: async (filters?: { from?: string; to?: string; q?: string; limit?: number; offset?: number }): Promise<ReportListResponse<EnquiryRegisterRow>> => {
            const params = new URLSearchParams();
            appendQueryParams(params, filters);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/reports/enquiries/register${suffix}`);
            if (!res.ok) throw await buildApiError(res, 'Failed to fetch enquiry register');
            return res.json();
        },
        getEnquiryConversion: async (filters?: { from?: string; to?: string }): Promise<ConversionRatioReport> => {
            const params = new URLSearchParams();
            appendQueryParams(params, filters);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/reports/enquiries/conversion${suffix}`);
            if (!res.ok) throw await buildApiError(res, 'Failed to fetch conversion report');
            return res.json();
        },
        getSalesPipeline: async (filters?: { from?: string; to?: string }): Promise<SalesPipelineReport> => {
            const params = new URLSearchParams();
            appendQueryParams(params, filters);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/reports/sales/pipeline${suffix}`);
            if (!res.ok) throw await buildApiError(res, 'Failed to fetch sales pipeline');
            return res.json();
        },
        getQuotationRegister: async (filters?: { from?: string; to?: string; q?: string; limit?: number; offset?: number }): Promise<ReportListResponse<QuotationRegisterRow>> => {
            const params = new URLSearchParams();
            appendQueryParams(params, filters);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/reports/quotations/register${suffix}`);
            if (!res.ok) throw await buildApiError(res, 'Failed to fetch quotation register');
            return res.json();
        },
        getEstimatedCostBreakdown: async (filters?: { from?: string; to?: string; q?: string; limit?: number; offset?: number }): Promise<ReportListResponse<EstimateCostBreakdownRow>> => {
            const params = new URLSearchParams();
            appendQueryParams(params, filters);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/reports/estimates/cost-breakdown${suffix}`);
            if (!res.ok) throw await buildApiError(res, 'Failed to fetch estimated cost breakdown');
            return res.json();
        },
        getProductionOutput: async (filters?: { from?: string; to?: string; group_by?: string; q?: string; limit?: number; offset?: number }): Promise<ProductionOutputResponse> => {
            const params = new URLSearchParams();
            appendQueryParams(params, filters);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/reports/production/output${suffix}`);
            if (!res.ok) throw await buildApiError(res, 'Failed to fetch production output report');
            return res.json();
        },
        getRawMaterialStock: async (filters?: { q?: string; limit?: number; offset?: number }): Promise<PagedReportResponse<StockValuationRecord>> => {
            const params = new URLSearchParams();
            appendQueryParams(params, filters);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/reports/inventory/raw-material-stock${suffix}`);
            if (!res.ok) throw await buildApiError(res, 'Failed to fetch raw material stock report');
            return res.json();
        },
        getMinimumStockAlert: async (filters?: { q?: string; limit?: number; offset?: number }): Promise<ReportListResponse<MinStockAlertRow>> => {
            const params = new URLSearchParams();
            appendQueryParams(params, filters);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/reports/inventory/min-stock-alert${suffix}`);
            if (!res.ok) throw await buildApiError(res, 'Failed to fetch minimum stock alert');
            return res.json();
        },
        getStockLedger: async (filters?: { from?: string; to?: string; q?: string; limit?: number; offset?: number }): Promise<ReportListResponse<StockMove>> => {
            const params = new URLSearchParams();
            appendQueryParams(params, filters);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/reports/inventory/stock-ledger${suffix}`);
            if (!res.ok) throw await buildApiError(res, 'Failed to fetch stock ledger report');
            return res.json();
        },
        getReceivablesAging: async (filters?: { as_of?: string; q?: string; limit?: number; offset?: number }): Promise<ReceivablesAgingResponse> => {
            const params = new URLSearchParams();
            appendQueryParams(params, filters);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/reports/financials/receivables-aging${suffix}`);
            if (!res.ok) throw await buildApiError(res, 'Failed to fetch receivables aging');
            return res.json();
        },
        getBalanceSheet: async (filters?: { from?: string; to?: string; limit?: number; offset?: number }): Promise<BalanceSheetResponse> => {
            const params = new URLSearchParams();
            appendQueryParams(params, filters);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/reports/financials/balance-sheet${suffix}`);
            if (!res.ok) throw await buildApiError(res, 'Failed to fetch balance sheet');
            return res.json();
        },
        getTrialBalance: async (filters?: { from?: string; to?: string; q?: string; limit?: number; offset?: number }): Promise<TrialBalanceReportResponse> => {
            const params = new URLSearchParams();
            appendQueryParams(params, filters);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/reports/financials/trial-balance${suffix}`);
            if (!res.ok) throw await buildApiError(res, 'Failed to fetch trial balance');
            return res.json();
        },
        getCashFlow: async (filters?: { from?: string; to?: string }): Promise<CashFlowReport> => {
            const params = new URLSearchParams();
            appendQueryParams(params, filters);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/reports/financials/cash-flow${suffix}`);
            if (!res.ok) throw await buildApiError(res, 'Failed to fetch cash flow report');
            return res.json();
        },
        getReleaseReadiness: async (): Promise<ReportReleaseReadinessResponse> => {
            const res = await authorizedFetch(`${API_BASE_URL}/reports/release-readiness`);
            if (!res.ok) throw await buildApiError(res, 'Failed to fetch report readiness');
            return res.json();
        },
        getPayablesAging: async (filters?: { as_of?: string; q?: string; limit?: number; offset?: number }): Promise<PayablesAgingResponse> => {
            const params = new URLSearchParams();
            appendQueryParams(params, filters);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/reports/financials/payables-aging${suffix}`);
            if (!res.ok) throw await buildApiError(res, 'Failed to fetch payables aging');
            return res.json();
        },
        getWipReport: async (filters?: { from?: string; to?: string; limit?: number; offset?: number }): Promise<WipReportResponse> => {
            const params = new URLSearchParams();
            appendQueryParams(params, filters);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/reports/production/wip${suffix}`);
            if (!res.ok) throw await buildApiError(res, 'Failed to fetch WIP report');
            return res.json();
        },
        getBreakEven: async (filters?: { from?: string; to?: string }): Promise<BreakEvenReport> => {
            const params = new URLSearchParams();
            appendQueryParams(params, filters);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/reports/financials/break-even${suffix}`);
            if (!res.ok) throw await buildApiError(res, 'Failed to fetch break-even report');
            return res.json();
        },
        getProductProfitability: async (filters?: { from?: string; to?: string; limit?: number; offset?: number }): Promise<ReportListResponse<ProductProfitabilityRow>> => {
            const params = new URLSearchParams();
            appendQueryParams(params, filters);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/reports/owner/product-profitability${suffix}`);
            if (!res.ok) throw await buildApiError(res, 'Failed to fetch product profitability');
            return res.json();
        },
        getGrcMaterialConsumption: async (filters?: { from?: string; to?: string; limit?: number; offset?: number }): Promise<ReportListResponse<GrcMaterialConsumptionRow>> => {
            const params = new URLSearchParams();
            appendQueryParams(params, filters);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/reports/production/grc-material-consumption${suffix}`);
            if (!res.ok) throw await buildApiError(res, 'Failed to fetch GRC material consumption');
            return res.json();
        },
        getEstimatedVsActual: async (filters?: { q?: string; limit?: number; offset?: number }): Promise<ReportListResponse<EstimatedVsActualRow>> => {
            const params = new URLSearchParams();
            appendQueryParams(params, filters);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/reports/jobs/estimated-vs-actual${suffix}`);
            if (!res.ok) throw await buildApiError(res, 'Failed to fetch estimated vs actual report');
            return res.json();
        },
        getJobProductionCosts: async (filters?: { from?: string; to?: string; q?: string; limit?: number; offset?: number }): Promise<ReportListResponse<JobProductionCostRow>> => {
            const params = new URLSearchParams();
            appendQueryParams(params, filters);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/reports/production/job-costs${suffix}`);
            if (!res.ok) throw await buildApiError(res, 'Failed to fetch job production costs');
            return res.json();
        },
        getMaterialConsumptionPerJob: async (filters?: { from?: string; to?: string; q?: string; limit?: number; offset?: number }): Promise<ReportListResponse<MaterialConsumptionRow>> => {
            const params = new URLSearchParams();
            appendQueryParams(params, filters);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/reports/production/material-consumption${suffix}`);
            if (!res.ok) throw await buildApiError(res, 'Failed to fetch material consumption per job');
            return res.json();
        },
        getContributionMarginPerJob: async (filters?: { q?: string; limit?: number; offset?: number }): Promise<ReportListResponse<ContributionMarginRow>> => {
            const params = new URLSearchParams();
            appendQueryParams(params, filters);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/reports/jobs/contribution-margin${suffix}`);
            if (!res.ok) throw await buildApiError(res, 'Failed to fetch contribution margin report');
            return res.json();
        },
        getOwnerDashboard: async (): Promise<OwnerDashboardReport> => {
            const res = await authorizedFetch(`${API_BASE_URL}/reports/owner/dashboard`);
            if (!res.ok) throw await buildApiError(res, 'Failed to fetch owner dashboard');
            return res.json();
        },
        getProfitAndLoss: async (filters?: { from?: string; to?: string; limit?: number; offset?: number }): Promise<ProfitAndLossReport> => {
            const params = new URLSearchParams();
            appendQueryParams(params, filters);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/reports/p-and-l${suffix}`);
            if (!res.ok) throw await buildApiError(res, 'Failed to fetch P&L report');
            return res.json();
        },
        exportCsv: async (endpoint: string, filters?: Record<string, string | number | undefined | null>, fallbackFilename = 'report.csv'): Promise<void> => {
            const params = new URLSearchParams();
            appendQueryParams(params, filters);
            const suffix = params.toString() ? `?${params.toString()}` : '';
            await downloadCsvFromApi(`${API_BASE_URL}${endpoint}${suffix}`, fallbackFilename);
        },
    },
    productionBucket: {
        getLogs: async (workOrderId: number) => {
            const res = await authorizedFetch(`${API_BASE_URL}/production-logs/wo/${workOrderId}`);
            return res.json();
        },
        addLog: async (log: any) => {
            const res = await authorizedFetch(`${API_BASE_URL}/production-logs`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(log)
            });
            if (!res.ok) throw new Error('Failed to add log');
            return res.json();
        },
        createDirectGRN: async (payload: any): Promise<GRN> => {
            const res = await authorizedFetch(`${API_BASE_URL}/purchasing/grn/direct`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) throw new Error('Failed to create direct GRN');
            return res.json();
        },
    },
    masterData: {
        list: async (table: string) => {
            const res = await authorizedFetch(`${API_BASE_URL}/master-data/${table}`);
            return res.json();
        },
        create: async (table: string, data: any) => {
            const res = await authorizedFetch(`${API_BASE_URL}/master-data/${table}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ data })
            });
            if (!res.ok) throw new Error('Failed to create record');
            return res.json();
        },
        update: async (table: string, id: number, data: any) => {
            const res = await authorizedFetch(`${API_BASE_URL}/master-data/${table}/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ data })
            });
            if (!res.ok) throw new Error('Failed to update record');
            return res.json();
        },
        delete: async (table: string, id: number) => {
            const res = await authorizedFetch(`${API_BASE_URL}/master-data/${table}/${id}`, {
                method: 'DELETE'
            });
            if (!res.ok) throw new Error('Failed to delete record');
            return res.json();
        }
    },
    overhead: {
        listMainCategories: async () => {
            const res = await authorizedFetch(`${API_BASE_URL}/overhead/main-categories`);
            return res.json();
        },
        listSubCategories: async (mainCategoryId?: number) => {
            const query = typeof mainCategoryId === 'number' ? `?oh_main_category_id=${mainCategoryId}` : '';
            const res = await authorizedFetch(`${API_BASE_URL}/overhead/sub-categories${query}`);
            return res.json();
        },
        createSubCategory: async (data: any) => {
            const res = await authorizedFetch(`${API_BASE_URL}/overhead/sub-categories`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!res.ok) throw new Error('Failed to create overhead sub category');
            return res.json();
        },
        updateSubCategory: async (id: number, data: any) => {
            const res = await authorizedFetch(`${API_BASE_URL}/overhead/sub-categories/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!res.ok) throw new Error('Failed to update overhead sub category');
            return res.json();
        },
        deleteSubCategory: async (id: number) => {
            const res = await authorizedFetch(`${API_BASE_URL}/overhead/sub-categories/${id}`, {
                method: 'DELETE'
            });
            if (!res.ok) throw new Error('Failed to delete overhead sub category');
            return res.json();
        },
        getSummary: async () => {
            const res = await authorizedFetch(`${API_BASE_URL}/overhead/summary`);
            return res.json();
        }
    },
    materials: {
        list: async () => {
            const res = await authorizedFetch(`${API_BASE_URL}/materials`);
            return res.json();
        },
        listGRC: async () => {
            const res = await authorizedFetch(`${API_BASE_URL}/materials/grc`);
            return res.json();
        },
        listGRP: async () => {
            const res = await authorizedFetch(`${API_BASE_URL}/materials/grp`);
            return res.json();
        },
        listBOM: async () => {
            const res = await authorizedFetch(`${API_BASE_URL}/materials/bom`);
            return res.json();
        },
        listFinishedGoods: async () => {
            const res = await authorizedFetch(`${API_BASE_URL}/materials/finished-goods`);
            return res.json();
        },
        createFinishedGood: async (data: any) => {
            const res = await authorizedFetch(`${API_BASE_URL}/materials/finished-goods`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!res.ok) throw new Error('Failed to create finished good');
            return res.json();
        },
        updateFinishedGood: async (itemId: number, data: any) => {
            const res = await authorizedFetch(`${API_BASE_URL}/materials/finished-goods/${itemId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!res.ok) throw new Error('Failed to update finished good');
            return res.json();
        },
        getMaterialCostProfiles: async (materialId: number) => {
            const res = await authorizedFetch(`${API_BASE_URL}/materials/${materialId}/cost-profiles`);
            if (!res.ok) throw new Error('Failed to load material cost profiles');
            return res.json();
        },
        updateMaterialCostProfiles: async (materialId: number, data: any) => {
            const res = await authorizedFetch(`${API_BASE_URL}/materials/${materialId}/cost-profiles`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!res.ok) throw new Error('Failed to update material cost profiles');
            return res.json();
        },
        listMaterialDocuments: async (materialId: number, category = 'SPECIFICATION') => {
            const res = await authorizedFetch(`${API_BASE_URL}/materials/${materialId}/documents?category=${encodeURIComponent(category)}`);
            if (!res.ok) throw new Error('Failed to load material documents');
            const data = await res.json();
            return Array.isArray(data?.documents) ? data.documents : [];
        },
        uploadMaterialDocument: async (materialId: number, payload: { file: File; category?: string; note?: string; mark_current?: boolean }) => {
            const formData = new FormData();
            formData.append('file', payload.file);
            formData.append('category', payload.category || 'SPECIFICATION');
            if (payload.note !== undefined) formData.append('note', payload.note);
            formData.append('mark_current', payload.mark_current === false ? 'false' : 'true');
            const res = await authorizedFetch(`${API_BASE_URL}/materials/${materialId}/documents`, { method: 'POST', body: formData });
            if (!res.ok) throw new Error('Failed to upload material document');
            return res.json();
        },
        setMaterialDocumentCurrent: async (materialId: number, docId: number) => {
            const res = await authorizedFetch(`${API_BASE_URL}/materials/${materialId}/documents/${docId}/set-current`, { method: 'PATCH' });
            if (!res.ok) throw new Error('Failed to set material document current');
            return res.json();
        },
        deleteMaterialDocument: async (materialId: number, docId: number) => {
            const res = await authorizedFetch(`${API_BASE_URL}/materials/${materialId}/documents/${docId}`, { method: 'DELETE' });
            if (!res.ok) throw new Error('Failed to delete material document');
        },
        previewMaterialDocument: (materialId: number, docId: number) => `${API_BASE_URL}/materials/${materialId}/documents/${docId}/preview`,
        downloadMaterialDocument: async (materialId: number, docId: number) => {
            const popup = window.open(`${API_BASE_URL}/materials/${materialId}/documents/${docId}/download`, '_blank');
            if (!popup) throw new Error('Pop-up blocked. Please allow pop-ups and try again.');
            popup.focus();
        },
        listFinishedGoodDocuments: async (itemId: number, category = 'SPECIFICATION') => {
            const res = await authorizedFetch(`${API_BASE_URL}/materials/finished-goods/${itemId}/documents?category=${encodeURIComponent(category)}`);
            if (!res.ok) throw new Error('Failed to load finished good documents');
            const data = await res.json();
            return Array.isArray(data?.documents) ? data.documents : [];
        },
        uploadFinishedGoodDocument: async (itemId: number, payload: { file: File; category?: string; note?: string; mark_current?: boolean }) => {
            const formData = new FormData();
            formData.append('file', payload.file);
            formData.append('category', payload.category || 'SPECIFICATION');
            if (payload.note !== undefined) formData.append('note', payload.note);
            formData.append('mark_current', payload.mark_current === false ? 'false' : 'true');
            const res = await authorizedFetch(`${API_BASE_URL}/materials/finished-goods/${itemId}/documents`, { method: 'POST', body: formData });
            if (!res.ok) throw new Error('Failed to upload finished good document');
            return res.json();
        },
        setFinishedGoodDocumentCurrent: async (itemId: number, docId: number) => {
            const res = await authorizedFetch(`${API_BASE_URL}/materials/finished-goods/${itemId}/documents/${docId}/set-current`, { method: 'PATCH' });
            if (!res.ok) throw new Error('Failed to set finished good document current');
            return res.json();
        },
        deleteFinishedGoodDocument: async (itemId: number, docId: number) => {
            const res = await authorizedFetch(`${API_BASE_URL}/materials/finished-goods/${itemId}/documents/${docId}`, { method: 'DELETE' });
            if (!res.ok) throw new Error('Failed to delete finished good document');
        },
        previewFinishedGoodDocument: (itemId: number, docId: number) => `${API_BASE_URL}/materials/finished-goods/${itemId}/documents/${docId}/preview`,
        downloadFinishedGoodDocument: async (itemId: number, docId: number) => {
            const popup = window.open(`${API_BASE_URL}/materials/finished-goods/${itemId}/documents/${docId}/download`, '_blank');
            if (!popup) throw new Error('Pop-up blocked. Please allow pop-ups and try again.');
            popup.focus();
        },
        getMaterialUomConversions: async (materialId: number) => {
            const res = await authorizedFetch(`${API_BASE_URL}/materials/${materialId}/uom-conversions`);
            if (!res.ok) throw new Error('Failed to load material UOM conversions');
            return res.json();
        },
        updateMaterialUomConversions: async (materialId: number, data: any) => {
            const res = await authorizedFetch(`${API_BASE_URL}/materials/${materialId}/uom-conversions`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!res.ok) throw new Error('Failed to update material UOM conversions');
            return res.json();
        },
        create: async (data: any) => {
            const res = await authorizedFetch(`${API_BASE_URL}/materials`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!res.ok) throw new Error('Failed to create material');
            return res.json();
        },
        update: async (id: number, data: any) => {
            const res = await authorizedFetch(`${API_BASE_URL}/materials/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!res.ok) throw new Error('Failed to update material');
            return res.json();
        },
        updateGRC: async (id: number, data: any) => {
            const res = await authorizedFetch(`${API_BASE_URL}/materials/grc/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!res.ok) throw new Error('Failed to update GRC cost');
            return res.json();
        },
        updateGRP: async (id: number, data: any) => {
            const res = await authorizedFetch(`${API_BASE_URL}/materials/grp/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!res.ok) throw new Error('Failed to update GRP cost');
            return res.json();
        },
        updateBOM: async (id: number, data: any) => {
            const res = await authorizedFetch(`${API_BASE_URL}/materials/bom/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!res.ok) throw new Error('Failed to update BOM rate');
            return res.json();
        },
        delete: async (id: number) => {
            const res = await authorizedFetch(`${API_BASE_URL}/materials/${id}`, {
                method: 'DELETE'
            });
            if (!res.ok) throw new Error('Failed to delete material');
            return res.json();
        },
    },
    designMix: {
        list: async () => {
            const res = await authorizedFetch(`${API_BASE_URL}/design-mix`);
            return res.json();
        },
        create: async (data: any) => {
            const res = await authorizedFetch(`${API_BASE_URL}/design-mix`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!res.ok) throw new Error('Failed to create design mix');
            return res.json();
        },
        update: async (id: number, data: any) => {
            const res = await authorizedFetch(`${API_BASE_URL}/design-mix/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!res.ok) throw new Error('Failed to update design mix');
            return res.json();
        },
        delete: async (id: number) => {
            const res = await authorizedFetch(`${API_BASE_URL}/design-mix/${id}`, {
                method: 'DELETE'
            });
            if (!res.ok) throw new Error('Failed to delete design mix');
            return res.json();
        }
    },
    system: {
        importLegacy: async (path: string) => {
            const res = await authorizedFetch(`${API_BASE_URL}/system/import-legacy`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ source_path: path })
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.detail || 'Import failed');
            }
            return res.json();
        },
        getAppMode: async () => {
            const res = await authorizedFetch(`${API_BASE_URL}/system/app-mode`);
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to load app mode');
            }
            return res.json();
        },
        setAppMode: async (mode: string) => {
            const res = await authorizedFetch(`${API_BASE_URL}/system/app-mode`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ mode })
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to update app mode');
            }
            return res.json();
        },
        listModules: async (): Promise<{ modules: any[] }> => {
            const res = await authorizedFetch(`${API_BASE_URL}/system/modules`);
            if (!res.ok) {
                // Fallback or empty if not found (during transition)
                console.warn('Failed to fetch modules, using defaults');
                return { modules: [] };
            }
            return res.json();
        },
        getNavigationProfile: async (): Promise<NavigationProfile> => {
            const res = await authorizedFetch(`${API_BASE_URL}/system/navigation/profile`);
            if (!res.ok) {
                return {
                    pinned_paths: [],
                    pinned_views: [],
                    collapsed_groups: [],
                    recent_paths: [],
                    home_role_layout: []
                };
            }
            return res.json();
        },
        updateNavigationProfile: async (payload: NavigationProfile): Promise<NavigationProfile> => {
            const res = await authorizedFetch(`${API_BASE_URL}/system/navigation/profile`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to update navigation profile');
            }
            return res.json();
        },
        trackNavigationEvent: async (payload: { path: string; label?: string; source?: string }) => {
            const res = await authorizedFetch(`${API_BASE_URL}/system/navigation/events`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to track navigation event');
            }
            return res.json();
        },
        getUsageAnalyticsStatus: async (): Promise<UsageAnalyticsConfig> => {
            const res = await authorizedFetch(`${API_BASE_URL}/system/usage/status`);
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to load usage analytics status');
            }
            return res.json();
        },
        getUsageAnalyticsConfig: async (): Promise<UsageAnalyticsConfig> => {
            const res = await authorizedFetch(`${API_BASE_URL}/system/usage/config`);
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to load usage analytics config');
            }
            return res.json();
        },
        updateUsageAnalyticsConfig: async (payload: UsageAnalyticsConfig): Promise<UsageAnalyticsConfig> => {
            const res = await authorizedFetch(`${API_BASE_URL}/system/usage/config`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to update usage analytics config');
            }
            return res.json();
        },
        startUsageVisit: async (payload: UsageVisitStartPayload): Promise<UsageVisitStartResponse | null> => {
            const res = await authorizedFetch(`${API_BASE_URL}/system/usage/visits`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (res.status === 204) return null;
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to start usage visit');
            }
            return res.json();
        },
        updateUsageVisit: async (visitId: number, payload: UsageVisitUpdatePayload, options?: { keepalive?: boolean }) => {
            const res = await authorizedFetch(`${API_BASE_URL}/system/usage/visits/${visitId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                keepalive: options?.keepalive,
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to update usage visit');
            }
            return res.json();
        },
        getUsageAnalyticsSummary: async (days = 7): Promise<UsageAnalyticsSummary> => {
            const res = await authorizedFetch(`${API_BASE_URL}/system/usage/summary?days=${encodeURIComponent(days)}`);
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to load usage analytics');
            }
            return res.json();
        }
    },
    external: {
        listQuotes: async (): Promise<Quote[]> => {
            if (USE_MOCK) return Promise.resolve(mockQuotes);
            const res = await authorizedFetch(`${API_BASE_URL}/integration/quotes`);
            return res.json();
        }
    },
    settings: {
        list: async (): Promise<SystemSetting[]> => {
            // Trailing slash avoids FastAPI 307 redirect from /settings -> /settings/
            // that can become mixed-content behind some proxies.
            const res = await authorizedFetch(`${API_BASE_URL}/settings/?limit=1000`);
            if (!res.ok) throw new Error('Failed to fetch settings');
            return res.json();
        },
        get: async (key: string): Promise<SystemSetting> => {
            const res = await authorizedFetch(`${API_BASE_URL}/settings/${key}`);
            if (!res.ok) throw new Error('Setting not found');
            return res.json();
        },
        update: async (key: string, data: Partial<SystemSetting>) => {
            const res = await authorizedFetch(`${API_BASE_URL}/settings/${key}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!res.ok) throw new Error('Failed to update setting');
            return res.json();
        },
        getPrintSignatureStamp: async (): Promise<PrintSignatureStampMetadata> => {
            const res = await authorizedFetch(`${API_BASE_URL}/settings/print-assets/signature-stamp`);
            if (!res.ok) throw await buildApiError(res, 'Failed to fetch print signature/stamp');
            return res.json();
        },
        uploadPrintSignatureStamp: async (file: File, enabled = true): Promise<PrintSignatureStampMetadata> => {
            const form = new FormData();
            form.append('file', file);
            form.append('enabled', enabled ? 'true' : 'false');
            const res = await authorizedFetch(`${API_BASE_URL}/settings/print-assets/signature-stamp`, {
                method: 'POST',
                body: form,
            });
            if (!res.ok) throw await buildApiError(res, 'Failed to upload print signature/stamp');
            return res.json();
        },
        removePrintSignatureStamp: async (): Promise<PrintSignatureStampMetadata> => {
            const res = await authorizedFetch(`${API_BASE_URL}/settings/print-assets/signature-stamp`, {
                method: 'DELETE',
            });
            if (!res.ok) throw await buildApiError(res, 'Failed to remove print signature/stamp');
            return res.json();
        },
        getDatabaseBackupStatus: async (): Promise<DatabaseBackupStatus> => {
            const res = await authorizedFetch(`${API_BASE_URL}/settings/database-backups/status`);
            if (!res.ok) throw await buildApiError(res, 'Failed to load database backup status');
            return res.json();
        },
        getStorageVisibilitySummary: async (): Promise<StorageVisibilitySummary> => {
            const res = await authorizedFetch(`${API_BASE_URL}/settings/storage-visibility/summary`);
            if (!res.ok) throw await buildApiError(res, 'Failed to load storage and sharing summary');
            return res.json();
        },
        exportDatabaseBackup: async (): Promise<void> => {
            const res = await authorizedFetch(`${API_BASE_URL}/settings/database-backups/export`);
            if (!res.ok) throw await buildApiError(res, 'Failed to export database backup');
            const blob = await res.blob();
            const disposition = res.headers.get('Content-Disposition') || '';
            const match = disposition.match(/filename="?([^";]+)"?/i);
            const filename = match ? match[1] : 'HADAF_Backup.zip';
            const objectUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(objectUrl);
        },
        restoreDatabaseBackup: async (file: File): Promise<DatabaseRestoreResponse> => {
            const form = new FormData();
            form.append('file', file);
            const res = await authorizedFetch(`${API_BASE_URL}/settings/database-backups/restore`, {
                method: 'POST',
                body: form,
            });
            if (!res.ok) throw await buildApiError(res, 'Failed to restore database backup');
            return res.json();
        },
        getReportingConfig: async (): Promise<ReportingConfig> => {
            const res = await authorizedFetch(`${API_BASE_URL}/settings/reporting/config`);
            if (!res.ok) throw await buildApiError(res, 'Failed to fetch reporting setup');
            return res.json();
        },
        updateReportingConfig: async (data: ReportingConfig): Promise<ReportingConfig> => {
            const res = await authorizedFetch(`${API_BASE_URL}/settings/reporting/config`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            if (!res.ok) throw await buildApiError(res, 'Failed to save reporting setup');
            return res.json();
        },
        getReportingReferenceData: async (): Promise<ReportingReferenceData> => {
            const res = await authorizedFetch(`${API_BASE_URL}/settings/reporting/reference-data`);
            if (!res.ok) throw await buildApiError(res, 'Failed to fetch reporting reference data');
            return res.json();
        },
        getReportingReadiness: async (): Promise<ReportingReadinessResponse> => {
            const res = await authorizedFetch(`${API_BASE_URL}/settings/reporting/readiness`);
            if (!res.ok) throw await buildApiError(res, 'Failed to fetch reporting readiness');
            return res.json();
        },
    },
    returns: {
        createSalesReturn: async (invoiceId: number, items: { invoice_detail_id: number, quantity: number }[]): Promise<any> => {
            // Credit Note
            const res = await authorizedFetch(`${API_BASE_URL}/sales/invoices/${invoiceId}/return`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items })
            });
            if (!res.ok) throw new Error('Failed to create Credit Note');
            return res.json();
        },
        createPurchaseReturn: async (billId: number, items: { bill_detail_id: number, quantity: number }[], remarks?: string): Promise<any> => {
            // Debit Note
            // Using the generic bill creation with negative values might be complex to coordinate from UI
            // So we use a dedicated endpoint if available, but for now let's assume a pattern
            // Or better, stick to the pattern found in backend checks:
            // The backend seemed to support it via specific create logic.
            // Let's assume a dedicated endpoint was added or will be added:
            const res = await authorizedFetch(`${API_BASE_URL}/purchasing/bills/${billId}/return`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items, remarks })
            });
            if (!res.ok) throw new Error('Failed to create Debit Note');
            return res.json();
        }
    }
};
